/* ===========================================================================
   Metal Label Configurator — popup/modal for Shopify product pages.

   Integration: when the product page's add-to-cart button is clicked, a modal
   opens to configure the label + see live pricing. On "Choose & Order" the
   buyer is sent to the cart (native) or checkout (draft order).

   It self-injects its markup, so a host page only needs a trigger:
     - any element with [data-metal-configurator]  → opens the modal
     - (Shopify) the theme's add-to-cart button, via window.PRICING_TRIGGER_SELECTOR
     - or call window.MetalConfigurator.open()
   =========================================================================== */
(function () {
  "use strict";

  var API_BASE = (window.PRICING_API_BASE || "http://localhost:3000").replace(/\/$/, "");
  var SHOPIFY_VARIANT_ID = window.PRICING_VARIANT_ID || null;
  var CHECKOUT_MODE = window.PRICING_CHECKOUT_MODE || (SHOPIFY_VARIANT_ID ? "cart" : "demo");
  var TRIGGER_SELECTOR = window.PRICING_TRIGGER_SELECTOR ||
    'form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] [name="add"]';
  var CART_URL = window.PRICING_CART_URL || "/cart";

  // ---- option catalogues -------------------------------------------------
  var SHAPES = [
    { id: "circle", label: "Circle" },
    { id: "square", label: "Square" },
    { id: "diecut", label: "Die-cut" },
  ];
  var MATERIALS = [
    { id: "aluminium", label: "Aluminium", g: ["#d9dde1", "#f6f8f9", "#b6bdc4"] },
    { id: "tin",       label: "Tin",       g: ["#cdd1d4", "#eef1f3", "#abb1b6"] },
    { id: "brass",     label: "Brass",     g: ["#c9a44e", "#f1da8d", "#9c7a2c"] },
    { id: "copper",    label: "Copper",    g: ["#b06a3a", "#e6a371", "#854726"] },
  ];
  var FINISHES = [
    { id: "glossy",  label: "Glossy" },
    { id: "printed", label: "Printed" },
    { id: "vintage", label: "Vintage" },
  ];
  var QUANTITIES = [250, 500, 750, 1000, 2500, 5000];

  var state = {
    shape: "circle", material: "brass", finish: "glossy",
    diameter_mm: 25, width_mm: 30, height_mm: 30, quantity: 250,
    coating: false, mold: false, logo: null, email: "",
  };

  var ordered = false; // becomes true once an order is submitted
  function validEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "")); }

  // CRM capture — stores email + config + logo so support can follow up with a mockup.
  function crmPayload(status) {
    return {
      email: state.email,
      status: status,
      config: buildRequest(),
      price: lastResult ? { total_price_eur: lastResult.total_price_eur, unit_price_eur: lastResult.unit_price_eur } : undefined,
      logo_dataurl: state.logo || undefined,
      source_url: location.href,
    };
  }
  function syncLead(status) {
    if (!validEmail(state.email) || ordered) return;
    fetch(API_BASE + "/lead", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(crmPayload(status)),
      keepalive: true, // survive page unload (abandoned capture)
    }).catch(function () {});
  }
  function recordOrder() {
    return fetch(API_BASE + "/order", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(crmPayload("ordered")),
    }).then(function () { ordered = true; }).catch(function () {});
  }

  // ---- markup (injected once) --------------------------------------------
  var TEMPLATE =
    '<div class="cfg-overlay" id="cfg-overlay" role="dialog" aria-modal="true" aria-label="Metal label configurator">' +
      '<div class="cfg-modal">' +
        '<div class="cfg-modal-head">' +
          '<div class="ti"><div class="eyebrow" id="cfg-eyebrow">Schild Inc · Custom Metal Labels</div>' +
          '<h2 id="cfg-heading">Design Your Metal Label</h2>' +
          '<div class="cfg-stepper">' +
            '<span class="cfg-step-dot" id="dot-1"><span class="n">1</span>Upload logo</span>' +
            '<span class="cfg-step-sep"></span>' +
            '<span class="cfg-step-dot" id="dot-2"><span class="n">2</span>Customize</span>' +
          '</div></div>' +
          '<button class="cfg-close" id="cfg-close" aria-label="Close">×</button>' +
        '</div>' +
        '<div class="cfg-modal-body">' +

          /* ---- STEP 1: upload ---- */
          '<div class="cfg-step" id="step-1">' +
            '<div class="upload-step">' +
              '<h3>Upload your logo or artwork</h3>' +
              '<p class="lead">We\'ll engrave or print this onto your metal label. Vector (SVG/PDF) or a transparent PNG works best.</p>' +
              '<label class="drop drop-lg" id="drop"><div class="di"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V4"/><path d="m7.5 8.5 4.5-4.5 4.5 4.5"/><path d="M5 14v3.5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V14"/></svg></div>' +
                '<strong id="drop-title">Click to upload or drag &amp; drop</strong>' +
                '<small id="drop-sub">PNG, JPG, SVG · transparent background recommended</small>' +
                '<input id="logo" type="file" accept="image/*" class="hidden"></label>' +
              '<div class="logo-preview hidden" id="logo-preview"><img id="logo-img" alt="Your logo"></div>' +
              '<div class="email-field">' +
                '<label for="email">Your email <span>— we\'ll send a free mockup &amp; quote</span></label>' +
                '<input id="email" type="email" inputmode="email" autocomplete="email" placeholder="you@company.com">' +
                '<small class="email-note" id="email-note"></small>' +
              '</div>' +
              '<div class="upload-actions">' +
                '<button class="btn-link" id="skip-upload">Skip — I\'ll send artwork later</button>' +
                '<button class="btn btn-primary" id="to-step-2">Continue →</button>' +
              '</div>' +
            '</div>' +
          '</div>' +

          /* ---- STEP 2: configure ---- */
          '<div class="cfg-step" id="step-2" hidden>' +
            '<div class="cfg-grid">' +
              '<section class="panel preview">' +
                '<div class="preview-stage" id="stage"></div>' +
                '<div class="badge-row" id="badges"></div>' +
                '<div class="preview-meta">' +
                  '<div><div class="k">Size</div><div class="v" id="m-size">—</div></div>' +
                  '<div><div class="k">Area</div><div class="v" id="m-area">—</div></div>' +
                  '<div><div class="k">Qty</div><div class="v" id="m-qty">—</div></div>' +
                '</div>' +
              '</section>' +
              '<section class="panel config">' +
                '<div class="field"><label class="flabel">Shape</label><div class="chips" id="shape"></div></div>' +
                '<div class="field"><label class="flabel">Material</label><div class="chips" id="material"></div></div>' +
                '<div class="field"><label class="flabel">Size <span class="hint" id="size-hint">millimetres</span></label>' +
                  '<div class="size-row" id="size-circle"><label class="num"><span>Diameter (mm)</span>' +
                    '<input id="diameter" type="number" min="5" max="120" step="1" value="25"></label></div>' +
                  '<div class="size-row hidden" id="size-rect">' +
                    '<label class="num"><span>Width (mm)</span><input id="width" type="number" min="5" max="200" step="1" value="30"></label>' +
                    '<label class="num"><span>Height (mm)</span><input id="height" type="number" min="5" max="200" step="1" value="30"></label>' +
                  '</div></div>' +
                '<div class="field"><label class="flabel">Surface finish</label><div class="chips" id="finish"></div></div>' +
                '<div class="field"><label class="flabel">Options</label><div class="toggles" id="options"></div></div>' +
                '<div class="field"><label class="flabel">Quantity</label><div class="chips" id="quantity"></div></div>' +
                '<div class="field"><label class="flabel">Logo / artwork</label>' +
                  '<label class="drop" id="drop2"><div class="di"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V4"/><path d="m7.5 8.5 4.5-4.5 4.5 4.5"/><path d="M5 14v3.5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V14"/></svg></div>' +
                    '<strong id="drop2-title">Upload logo</strong>' +
                    '<small id="drop2-sub">Change or add your artwork</small>' +
                    '<input id="logo2" type="file" accept="image/*" class="hidden"></label></div>' +
              '</section>' +
            '</div>' +
          '</div>' +
        '</div>' +

        '<div class="cfg-modal-foot">' +
          /* footer for step 1 */
          '<div class="pricebar-inner" id="foot-1">' +
            '<div class="price-main"><div class="total" style="font-size:18px">Step 1 of 2</div>' +
              '<div class="per">Add your artwork, then customize</div></div>' +
            '<div class="price-spacer"></div>' +
            '<button class="btn btn-primary" id="to-step-2b">Continue →</button>' +
          '</div>' +
          /* footer for step 2 */
          '<div id="foot-2" hidden>' +
            '<div class="breakdown hidden" id="breakdown"></div>' +
            '<div class="pricebar-inner">' +
              '<button class="btn btn-ghost" id="to-step-1">← Back</button>' +
              '<div class="price-main"><div class="total loading" id="total">€—</div>' +
                '<div class="per" id="per">calculating…</div></div>' +
              '<button class="btn-link" id="toggle-breakdown">View breakdown ▾</button>' +
              '<div class="price-spacer"></div>' +
              '<button class="btn btn-primary" id="addcart">Add to Cart ↗</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>' +
    '</div>';

  var $ = function (id) { return document.getElementById(id); };
  function matGrad(id) { return (MATERIALS.find(function (m) { return m.id === id; }) || MATERIALS[0]).g; }
  function labelOf(arr, id) { var x = arr.find(function (a) { return a.id === id; }); return x ? x.label : id; }

  var injected = false, lastResult = null, timer = null;

  function inject() {
    if (injected) return;
    var host = document.createElement("div");
    host.innerHTML = TEMPLATE;
    document.body.appendChild(host.firstChild);
    if (window.PRICING_EYEBROW) $("cfg-eyebrow").textContent = window.PRICING_EYEBROW;
    if (window.PRICING_HEADING) $("cfg-heading").textContent = window.PRICING_HEADING;
    if (window.PRICING_CTA) $("addcart").firstChild.textContent = window.PRICING_CTA + " ";
    wire();
    injected = true;
  }

  // ---- open / close ------------------------------------------------------
  function open() {
    inject();
    ordered = false; // new session
    document.body.classList.add("cfg-open");
    requestAnimationFrame(function () { $("cfg-overlay").classList.add("show"); });
    goStep(1);
  }
  function close() {
    syncLead("abandoned"); // capture the email for follow-up even if they bail
    var ov = $("cfg-overlay");
    if (ov) ov.classList.remove("show");
    document.body.classList.remove("cfg-open");
  }

  // ---- chip / toggle builders --------------------------------------------
  function buildChips(containerId, items, getActive, onPick, withDot) {
    var box = $(containerId); box.innerHTML = "";
    items.forEach(function (it) {
      var b = document.createElement("button");
      b.className = "chip"; b.type = "button";
      b.setAttribute("aria-pressed", String(getActive(it)));
      if (withDot) {
        var dot = document.createElement("span"); dot.className = "dot";
        dot.style.background = "linear-gradient(135deg," + it.g[1] + "," + it.g[0] + " 45%," + it.g[2] + ")";
        b.appendChild(dot);
      }
      b.appendChild(document.createTextNode(it.label != null ? it.label : it));
      b.onclick = function () { onPick(it); };
      box.appendChild(b);
    });
  }
  function refreshPressed(containerId, getActive, items) {
    [].forEach.call($(containerId).children, function (b, i) {
      b.setAttribute("aria-pressed", String(getActive(items[i])));
    });
  }
  function buildOptions() {
    var box = $("options"); box.innerHTML = "";
    [
      { key: "coating", title: "Outdoor coating", sub: "Protective lacquer for outdoor use" },
      { key: "mold", title: "Custom die / mold", sub: "One-time tooling for a custom cut (+€40)" },
    ].forEach(function (d) {
      var row = document.createElement("div"); row.className = "toggle";
      row.innerHTML = '<div class="t-txt"><strong>' + d.title + "</strong><small>" + d.sub + "</small></div>";
      var sw = document.createElement("button");
      sw.className = "sw"; sw.type = "button"; sw.dataset.key = d.key;
      sw.setAttribute("aria-pressed", String(state[d.key]));
      sw.onclick = function () { state[d.key] = !state[d.key]; sw.setAttribute("aria-pressed", String(state[d.key])); update(); };
      row.appendChild(sw); box.appendChild(row);
    });
  }

  // ---- SVG preview (engraved metal label on wood) ------------------------
  // geometry: plate outline, recessed field, decorative inner line + field box
  function plateGeom() {
    if (state.shape === "circle") {
      return {
        outer: function (a) { return '<circle cx="100" cy="100" r="93" ' + a + "/>"; },
        field: function (a) { return '<circle cx="100" cy="100" r="71" ' + a + "/>"; },
        ring: function (a) { return '<circle cx="100" cy="100" r="80" ' + a + "/>"; },
        box: { x: 38, y: 38, w: 124, h: 124 },
      };
    }
    if (state.shape === "diecut") {
      var d = "M100 10 C150 14 168 40 186 64 C198 80 196 116 184 134 C168 160 150 188 100 192 C50 188 32 160 16 134 C4 116 2 80 14 64 C32 40 50 14 100 10 Z";
      return {
        outer: function (a) { return '<path d="' + d + '" ' + a + "/>"; },
        field: function (a) { return '<path d="' + d + '" transform="translate(100 101) scale(.78) translate(-100 -101)" ' + a + "/>"; },
        ring: function (a) { return '<path d="' + d + '" transform="translate(100 101) scale(.89) translate(-100 -101)" ' + a + "/>"; },
        box: { x: 42, y: 46, w: 116, h: 112 },
      };
    }
    // square / rectangle — aspect-aware to the entered W × H
    var w = state.width_mm || 1, h = state.height_mm || 1, maxd = 182, bw, bh;
    if (w >= h) { bw = maxd; bh = maxd * h / w; } else { bh = maxd; bw = maxd * w / h; }
    bw = Math.max(bw, 96); bh = Math.max(bh, 96);
    var x = (200 - bw) / 2, y = (206 - bh) / 2, ins = 15;
    return {
      outer: function (a) { return '<rect x="' + x + '" y="' + y + '" width="' + bw + '" height="' + bh + '" rx="13" ' + a + "/>"; },
      field: function (a) { return '<rect x="' + (x + ins) + '" y="' + (y + ins) + '" width="' + (bw - 2 * ins) + '" height="' + (bh - 2 * ins) + '" rx="6" ' + a + "/>"; },
      ring: function (a) { return '<rect x="' + (x + 6) + '" y="' + (y + 6) + '" width="' + (bw - 12) + '" height="' + (bh - 12) + '" rx="9" ' + a + "/>"; },
      box: { x: x + ins + 6, y: y + ins + 6, w: bw - 2 * ins - 12, h: bh - 2 * ins - 12 },
    };
  }

  function renderPreview() {
    var g = matGrad(state.material);
    var light = g[1], mid = g[0], dark = g[2];
    var vintage = state.finish === "vintage", glossy = state.finish === "glossy";
    var G = plateGeom(), b = G.box;

    // logo / placeholder rendered as engraved metal (alpha → metal relief)
    var logo = state.logo
      ? '<image href="' + state.logo + '" x="' + b.x + '" y="' + b.y + '" width="' + b.w + '" height="' + b.h +
        '" preserveAspectRatio="xMidYMid meet" filter="url(#engrave)"/>'
      : '<text x="100" y="105" text-anchor="middle" font-family="Montserrat,sans-serif" font-weight="800" ' +
        'font-size="15" letter-spacing="1.5" fill="#000" filter="url(#engrave)">YOUR LOGO</text>';

    var patina = vintage ? G.field('fill="url(#patina)"') : "";
    var gloss = (glossy || state.coating)
      ? G.outer('fill="#ffffff" opacity="' + (glossy ? ".18" : ".12") + '" transform="translate(0 -6) scale(1 .42)" transform-origin="100 60"') : "";

    $("stage").innerHTML =
      '<svg viewBox="0 0 200 206" xmlns="http://www.w3.org/2000/svg"><defs>' +
        '<linearGradient id="bezel" x1="0" y1="0" x2="0.2" y2="1">' +
          '<stop offset="0%" stop-color="#ffffff" stop-opacity=".7"/><stop offset="14%" stop-color="' + light +
          '"/><stop offset="55%" stop-color="' + mid + '"/><stop offset="100%" stop-color="' + dark + '"/></linearGradient>' +
        '<radialGradient id="fieldg" cx="42%" cy="32%" r="80%">' +
          '<stop offset="0%" stop-color="' + light + '"/><stop offset="55%" stop-color="' + mid +
          '"/><stop offset="100%" stop-color="' + dark + '"/></radialGradient>' +
        '<radialGradient id="patina" cx="50%" cy="42%" r="74%">' +
          '<stop offset="34%" stop-color="#2a2008" stop-opacity="0"/><stop offset="100%" stop-color="#181203" stop-opacity=".66"/></radialGradient>' +
        '<filter id="ds" x="-45%" y="-45%" width="190%" height="190%">' +
          '<feDropShadow dx="0" dy="8" stdDeviation="7" flood-color="#000000" flood-opacity=".55"/></filter>' +
        // engrave: take logo alpha → fill metal tone + specular relief + cast shadow
        '<filter id="engrave" x="-12%" y="-12%" width="124%" height="124%">' +
          '<feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="a"/>' +
          '<feFlood flood-color="' + mid + '" result="fl"/><feComposite in="fl" in2="a" operator="in" result="base"/>' +
          '<feOffset in="a" dx="0.5" dy="0.9" result="so"/><feFlood flood-color="#000000" flood-opacity=".45" result="sf"/>' +
          '<feComposite in="sf" in2="so" operator="in" result="sh"/>' +
          '<feGaussianBlur in="a" stdDeviation="0.8" result="bl"/>' +
          '<feSpecularLighting in="bl" surfaceScale="3.2" specularConstant="1" specularExponent="22" lighting-color="#ffffff" result="sp">' +
            '<feDistantLight azimuth="235" elevation="54"/></feSpecularLighting>' +
          '<feComposite in="sp" in2="a" operator="in" result="spc"/>' +
          '<feMerge><feMergeNode in="sh"/><feMergeNode in="base"/><feMergeNode in="spc"/></feMerge></filter>' +
        "<clipPath id='clip'>" + G.field("") + "</clipPath></defs>" +
        '<g filter="url(#ds)">' +
          G.outer('fill="url(#bezel)"') +                                            /* raised border */
          G.field('fill="#000000" opacity=".3" transform="translate(0 1.6)"') +      /* field recess shadow */
          G.field('fill="url(#fieldg)"') +                                           /* recessed metal field */
          '<g clip-path="url(#clip)">' + logo + patina + gloss + "</g>" +
          G.ring('fill="none" stroke="#ffffff" stroke-opacity=".4" stroke-width="1"') +   /* inner accent line */
          G.ring('fill="none" stroke="#000000" stroke-opacity=".22" stroke-width="1" transform="translate(0 1)"') +
          G.outer('fill="none" stroke="#ffffff" stroke-opacity=".5" stroke-width="1.2"') + /* top edge highlight */
        "</g></svg>";

    var badges = [labelOf(FINISHES, state.finish)];
    if (state.coating) badges.push("Outdoor coating");
    if (state.shape === "diecut") badges.push("Custom die");
    $("badges").innerHTML = badges.map(function (b) { return '<span class="fbadge">' + b + "</span>"; }).join("");
    $("m-size").textContent = state.shape === "circle" ? "Ø " + state.diameter_mm + " mm" : state.width_mm + " × " + state.height_mm + " mm";
    $("m-qty").textContent = state.quantity.toLocaleString();
  }

  // ---- pricing -----------------------------------------------------------
  function buildRequest() {
    var body = {
      shape: state.shape, material: state.material, quantity: state.quantity,
      options: {
        printed: state.finish === "printed", vintage: state.finish === "vintage",
        coating: state.coating, mold: state.shape === "diecut" ? true : state.mold,
      },
    };
    if (state.shape === "circle") body.diameter_mm = state.diameter_mm;
    else { body.width_mm = state.width_mm; body.height_mm = state.height_mm; }
    return body;
  }
  function update() {
    renderPreview();
    $("total").classList.add("loading"); $("per").textContent = "calculating…";
    clearTimeout(timer); timer = setTimeout(fetchPrice, 220);
  }
  function fetchPrice() {
    fetch(API_BASE + "/calc-price?model=json", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(buildRequest()),
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        if (!res.ok || !res.j.ok) throw new Error(res.j.error || "Pricing error");
        lastResult = res.j; renderPrice(res.j);
      })
      .catch(function (e) {
        lastResult = null; $("total").classList.remove("loading"); $("total").textContent = "—";
        $("per").innerHTML = '<span class="err">' + apiHint(e) + "</span>";
      });
  }
  function apiHint(e) {
    return /Failed to fetch|NetworkError/.test(String(e.message))
      ? "Pricing API offline — check " + API_BASE : e.message;
  }
  function renderPrice(j) {
    $("total").classList.remove("loading");
    $("total").textContent = j.display.total; $("per").textContent = j.display.unit;
    $("m-area").textContent = j.geometry.area_cm2.toFixed(1) + " cm²";
    var b = j.breakdown;
    var rows = [
      ["Metal subtotal (" + j.quantity + " × €" + b.per_piece_eur.toFixed(3) + ")", "€" + b.metal_subtotal_eur.toFixed(2)],
      ["Setup / plate fee", "€" + b.setup_fee_eur.toFixed(2)],
    ];
    if (b.diecut_fee_eur) rows.push(["Custom die / mold", "€" + b.diecut_fee_eur.toFixed(2)]);
    var html = "<table>" + rows.map(function (r) { return "<tr><td>" + r[0] + '</td><td class="v">' + r[1] + "</td></tr>"; }).join("") +
      '<tr class="sum"><td>Total (' + j.quantity + ' labels)</td><td class="v">' + j.display.total + "</td></tr></table>";
    var info = (j.flags || []).filter(function (f) { return /clamped|fell_back/.test(f); });
    if (info.length) html += '<div class="flagline">⚠︎ ' + info.join(" · ") + "</div>";
    $("breakdown").innerHTML = html;
  }

  // ---- checkout ----------------------------------------------------------
  function addToCart() {
    if (!lastResult) return;
    var props = lastResult.shopify_properties || {};
    if (state.logo) props._logo_uploaded = "yes";
    if (state.email) props.Email = state.email;
    var btn = $("addcart");
    btn.disabled = true;
    // record the order in the CRM first, then proceed to cart/checkout
    recordOrder().then(function () { doCheckout(props, btn); });
  }

  function doCheckout(props, btn) {
    if (CHECKOUT_MODE === "draft") {
      btn.disabled = true;
      fetch(API_BASE + "/draft-order", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(buildRequest()),
      })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (j.ok && j.invoice_url) window.location.href = j.invoice_url;
          else throw new Error(j.error || "Checkout failed");
        })
        .catch(function (e) { alert(e.message); btn.disabled = false; });
    } else if (CHECKOUT_MODE === "cart" && SHOPIFY_VARIANT_ID) {
      btn.disabled = true;
      fetch("/cart/add.js", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: SHOPIFY_VARIANT_ID, quantity: 1, properties: props }] }),
      })
        .then(function (r) { return r.json(); })
        .then(function () { window.location.href = CART_URL; })
        .catch(function () { alert("Could not add to cart."); btn.disabled = false; });
    } else {
      btn.disabled = false;
      close();
      alert("✓ Order captured (demo)\n\n" + lastResult.display.total + "  (" + lastResult.display.unit + ")\n" +
        state.shape + " · " + state.material + " · " + state.finish + " · qty " + state.quantity +
        (state.email ? "\n" + state.email : "") +
        "\n\nSaved to CRM. Line-item properties logged to console.");
      console.log("Add-to-cart payload:", { quantity: 1, properties: props });
    }
  }

  // ---- wiring (inside modal) ---------------------------------------------
  function wire() {
    buildChips("shape", SHAPES, function (s) { return state.shape === s.id; }, function (s) {
      state.shape = s.id;
      $("size-circle").classList.toggle("hidden", s.id !== "circle");
      $("size-rect").classList.toggle("hidden", s.id === "circle");
      $("size-hint").textContent = s.id === "circle" ? "diameter" : "width × height";
      refreshPressed("shape", function (x) { return state.shape === x.id; }, SHAPES); update();
    });
    buildChips("material", MATERIALS, function (m) { return state.material === m.id; }, function (m) {
      state.material = m.id; refreshPressed("material", function (x) { return state.material === x.id; }, MATERIALS); update();
    }, true);
    buildChips("finish", FINISHES, function (f) { return state.finish === f.id; }, function (f) {
      state.finish = f.id; refreshPressed("finish", function (x) { return state.finish === x.id; }, FINISHES); update();
    });
    buildChips("quantity", QUANTITIES, function (q) { return state.quantity === q; }, function (q) {
      state.quantity = q; refreshPressed("quantity", function (x) { return state.quantity === x; }, QUANTITIES); update();
    });
    buildOptions();

    ["diameter", "width", "height"].forEach(function (id) {
      $(id).addEventListener("input", function (e) {
        var v = parseFloat(e.target.value) || 0;
        if (id === "diameter") state.diameter_mm = v;
        if (id === "width") state.width_mm = v;
        if (id === "height") state.height_mm = v;
        update();
      });
    });

    bindDrop("drop", "logo");
    bindDrop("drop2", "logo2");

    // email capture (debounced lead sync once valid)
    var emailTimer = null;
    $("email").addEventListener("input", function (e) {
      state.email = e.target.value.trim();
      var note = $("email-note");
      clearTimeout(emailTimer);
      if (validEmail(state.email)) {
        note.textContent = "✓ We'll send your mockup here";
        note.className = "email-note ok";
        emailTimer = setTimeout(function () { syncLead("lead"); }, 700);
      } else {
        note.textContent = "";
        note.className = "email-note";
      }
    });

    // step navigation
    $("to-step-2").addEventListener("click", function () { syncLead("lead"); goStep(2); });
    $("to-step-2b").addEventListener("click", function () { syncLead("lead"); goStep(2); });
    $("skip-upload").addEventListener("click", function () { goStep(2); });
    $("to-step-1").addEventListener("click", function () { goStep(1); });

    $("toggle-breakdown").addEventListener("click", function () {
      var bd = $("breakdown"); bd.classList.toggle("hidden");
      this.textContent = bd.classList.contains("hidden") ? "View breakdown ▾" : "Hide breakdown ▴";
    });
    $("addcart").addEventListener("click", addToCart);
    $("cfg-close").addEventListener("click", close);
    $("cfg-overlay").addEventListener("click", function (e) { if (e.target === this) close(); });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && document.body.classList.contains("cfg-open")) close();
    });
  }

  // step wizard
  function goStep(n) {
    $("step-1").hidden = n !== 1;
    $("step-2").hidden = n !== 2;
    $("foot-1").hidden = n !== 1;
    $("foot-2").hidden = n !== 2;
    $("dot-1").className = "cfg-step-dot " + (n === 1 ? "active" : "done");
    $("dot-2").className = "cfg-step-dot " + (n === 2 ? "active" : "");
    var body = document.querySelector(".cfg-modal-body");
    if (body) body.scrollTop = 0;
    if (n === 2) update();
  }

  function bindDrop(dropId, inputId) {
    var drop = $(dropId), inputEl = $(inputId);
    if (!drop) return;
    inputEl.addEventListener("change", function (e) { handleFile(e.target.files[0]); });
    ["dragover", "dragenter"].forEach(function (ev) {
      drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.add("drag"); });
    });
    ["dragleave", "drop"].forEach(function (ev) {
      drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.remove("drag"); });
    });
    drop.addEventListener("drop", function (e) { handleFile(e.dataTransfer.files[0]); });
  }

  function handleFile(file) {
    if (!file) return;
    var reader = new FileReader();
    reader.onload = function (e) {
      state.logo = e.target.result;
      // step-1 dropzone + preview
      var d1 = $("drop"); if (d1) { d1.classList.add("has-file"); $("drop-title").textContent = file.name; $("drop-sub").textContent = "Click to replace"; }
      var prev = $("logo-preview"); if (prev) { prev.classList.remove("hidden"); $("logo-img").src = e.target.result; }
      // step-2 dropzone label
      var d2 = $("drop2"); if (d2) { d2.classList.add("has-file"); $("drop2-title").textContent = file.name; $("drop2-sub").textContent = "Click to replace"; }
      renderPreview();
    };
    reader.readAsDataURL(file);
  }

  // ---- triggers on the host page -----------------------------------------
  function bindTriggers() {
    // explicit triggers
    document.querySelectorAll("[data-metal-configurator]").forEach(function (el) {
      el.addEventListener("click", function (e) { e.preventDefault(); open(); });
    });
    // intercept the theme's add-to-cart button(s)
    if (CHECKOUT_MODE !== "demo" || window.PRICING_FORCE_TRIGGER) {
      try {
        document.querySelectorAll(TRIGGER_SELECTOR).forEach(function (el) {
          el.addEventListener("click", function (e) { e.preventDefault(); e.stopPropagation(); open(); }, true);
        });
      } catch (_) {}
    }
  }

  window.MetalConfigurator = { open: open, close: close };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", bindTriggers);
  else bindTriggers();
})();
